import React, { useState } from 'react';
import {
  XMarkIcon,
  BellIcon,
  SpeakerWaveIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface ChatSettingsProps {
  onClose: () => void;
}

const ChatSettings: React.FC<ChatSettingsProps> = ({ onClose }) => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('chat_settings');
    return saved ? JSON.parse(saved) : {
      enabled: true,
      sound: true,
      desktop: true,
    };
  });

  const [soundVolume, setSoundVolume] = useState(() => {
    const saved = localStorage.getItem('chat_sound_volume');
    return saved ? parseInt(saved) : 70;
  });

  const saveSettings = () => {
    localStorage.setItem('chat_settings', JSON.stringify(settings));
    localStorage.setItem('chat_sound_volume', soundVolume.toString());
    onClose();
  };

  const toggleSetting = (key: string) => {
    setSettings((prev: any) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">⚙️ Configurações</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5 overflow-y-auto max-h-[calc(80vh-130px)]">
          {/* Notificações */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-wide">
              <BellIcon className="h-4 w-4" />
              Notificações
            </h3>
            
            <div className="space-y-2">
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <div>
                  <div className="font-medium text-gray-900 text-sm">Ativar notificações</div>
                  <div className="text-xs text-gray-500">Receber alertas de novas mensagens</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={() => toggleSetting('enabled')}
                  className="h-5 w-5 text-gray-800 rounded accent-gray-800"
                />
              </label>

              <label className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors ${!settings.enabled ? 'opacity-50' : ''}`}>
                <div>
                  <div className="font-medium text-gray-900 text-sm">Som de notificação</div>
                  <div className="text-xs text-gray-500">Tocar som ao receber mensagens</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.sound}
                  onChange={() => toggleSetting('sound')}
                  disabled={!settings.enabled}
                  className="h-5 w-5 text-gray-800 rounded accent-gray-800 disabled:opacity-50"
                />
              </label>

              <label className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors ${!settings.enabled ? 'opacity-50' : ''}`}>
                <div>
                  <div className="font-medium text-gray-900 text-sm">Notificações do navegador</div>
                  <div className="text-xs text-gray-500">Mostrar pop-ups mesmo quando minimizado</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.desktop}
                  onChange={() => toggleSetting('desktop')}
                  disabled={!settings.enabled}
                  className="h-5 w-5 text-gray-800 rounded accent-gray-800 disabled:opacity-50"
                />
              </label>
            </div>
          </div>

          {/* Volume do Som */}
          {settings.sound && settings.enabled && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2 uppercase tracking-wide">
                <SpeakerWaveIcon className="h-4 w-4" />
                Volume do Som
              </h3>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Volume</span>
                  <span className="text-xs font-medium text-gray-900">{soundVolume}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={soundVolume}
                  onChange={(e) => setSoundVolume(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-800"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={saveSettings}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2 text-sm"
          >
            <CheckIcon className="h-4 w-4" />
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatSettings;
